import { useState } from 'react';
import { message } from 'antd';
import { BellOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'components/ui';
import {
  AtlasGqlDispatchMode,
  useSubscribeToNotificationsForWorkContainerMutation,
  useUnsubscribeFromNotificationsForWorkContainerMutation,
  useWorkContainerNotificationSubscriptionSettingsQuery,
} from 'types/atlas-graphql';
import { StyledButton } from './NotificationSubscriptions.style';

type Props = {
  containerIds: string[];
  afterSubscribe?: () => void;
};

export const BulkNotificationSubscriptionButton: React.FC<Props> = ({
  containerIds,
  afterSubscribe,
}) => {
  const [subscribed, setSubscribed] = useState<boolean>(false);

  const workContainerNotificationCopy =
    'You will receive Notifications for new Comments, change in Status, and new Attachments';

  // TODO: to support in-app notifications, duplicate this query with AtlasGqlDispatchMode.InApp and treat with an "or"
  const { data: workContainerEmailSubscriptionSettingsData } =
    useWorkContainerNotificationSubscriptionSettingsQuery({
      variables: { input: { dispatchMode: AtlasGqlDispatchMode.Email } },
    });
  const notificationsEnabled =
    !!workContainerEmailSubscriptionSettingsData?.workContainerNotificationSubscriptionSettings
      .enabled;

  const [subscribe, { loading: subscribeLoading }] =
    useSubscribeToNotificationsForWorkContainerMutation({});
  const [unsubscribe, { loading: unsubscribeLoading }] =
    useUnsubscribeFromNotificationsForWorkContainerMutation({});

  const handleSubscribe = async () => {
    try {
      await Promise.all(
        containerIds.map(containerId => subscribe({ variables: { input: { containerId } } }))
      );

      setSubscribed(true);

      if (!!afterSubscribe) {
        afterSubscribe();
      }
    } catch (error) {
      console.error(error);
      message.error('There was an issue subscribing. Please try again');
    }
  };
  const handleUnsubscribe = async () => {
    try {
      await Promise.all(
        containerIds.map(containerId => unsubscribe({ variables: { input: { containerId } } }))
      );

      setSubscribed(false);
    } catch (error) {
      console.error(error);
      message.error('There was an issue unsubscribing. Please try again');
    }
  };

  if (subscribed) {
    return (
      <Tooltip
        title={
          notificationsEnabled ? (
            <>
              You are subscribed to Notifications for{' '}
              {containerIds.length === 1 ? 'this Task' : 'each of these Tasks'}.{' '}
              {workContainerNotificationCopy}. Click to Unsubscribe.
            </>
          ) : (
            <>
              You're subscribed to Notifications for{' '}
              {containerIds.length === 1 ? 'this Task' : 'these Tasks'} but have Notifications
              disabled. You can re-enable notifications in{' '}
              <a href="/settings/notification-settings" target="_blank">
                settings
              </a>
              .
            </>
          )
        }
      >
        <span>
          <StyledButton
            onClick={handleUnsubscribe}
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={unsubscribeLoading}
            disabled={!notificationsEnabled}
          >
            Subscribed
          </StyledButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        notificationsEnabled ? (
          <>
            Click to Subscribe to Notifications for{' '}
            {containerIds.length === 1 ? 'this Task' : 'each of these Tasks'}.{' '}
            {workContainerNotificationCopy}.
          </>
        ) : (
          <>
            To subscribe to Notifications, enable Work Container Notifications in{' '}
            <a href="/settings/notification-settings" target="_blank">
              settings
            </a>
            .
          </>
        )
      }
    >
      <span>
        <StyledButton
          data-testid="accept-bulk-subscription-button"
          style={{ width: '100%' }}
          onClick={handleSubscribe}
          icon={<BellOutlined />}
          loading={subscribeLoading}
          disabled={!notificationsEnabled}
        >
          Subscribe
        </StyledButton>
      </span>
    </Tooltip>
  );
};
